import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Store, ArrowLeft } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { formatApiErrorDetail } from "@/lib/api";
import { RANGER_AVATAR } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import Seo from "@/components/Seo";

const inputCls = "w-full bg-transparent border border-white/15 px-3 py-2.5 text-sm text-white outline-none focus:border-[#00E5FF]";

export default function OwnerAuth() {
  const { login, register, user, loading } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("register");
  const [f, setF] = useState({ name: "", email: "", password: "", business_name: "", account_type: "owner" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!loading && user) nav(user.role === "operator" ? "/operator" : "/owner"); }, [user, loading, nav]);

  const submit = async (e) => {
    e.preventDefault(); setError(""); setBusy(true);
    try {
      let u;
      if (mode === "register") u = await register(f);
      else u = await login(f.email, f.password);
      nav(u?.role === "operator" ? "/operator" : "/owner");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] grain flex items-center justify-center px-4 py-10">
      <Seo title="Business Login" description="Sign in or create your Visit RiNo business account to manage your listing, events, and offers." />
      <div className="relative z-10 w-full max-w-md border border-white/10 bg-[#141414] p-8">
        <Link to="/" className="inline-flex items-center gap-2 text-neutral-400 hover:text-white text-sm mb-6"><ArrowLeft size={15} /> Back to site</Link>
        <div className="flex items-center gap-3 mb-6">
          <img src={RANGER_AVATAR} alt="" className="w-12 h-12 object-cover border border-[#FF0055]" />
          <div>
            <div className="font-display font-extrabold text-xl text-white">Business Portal</div>
            <div className="font-accent text-[10px] text-[#00E5FF]">Manage your RiNo presence</div>
          </div>
        </div>

        <div className="flex border border-white/15 mb-6">
          {["register", "login"].map((m) => (
            <button key={m} onClick={() => setMode(m)} data-testid={`owner-mode-${m}`}
              className={`flex-1 py-2.5 text-sm font-medium transition-colors ${mode === m ? "bg-[#FF0055] text-white" : "text-neutral-400 hover:text-white"}`}>
              {m === "register" ? "Sign Up" : "Sign In"}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <>
              <div>
                <label className="text-neutral-400 text-xs mb-1.5 block">I'm signing up as a…</label>
                <div className="grid grid-cols-2 gap-2">
                  {[["owner", "Business / Venue"], ["operator", "Tour / Experience Operator"]].map(([val, label]) => (
                    <button type="button" key={val} onClick={() => setF({ ...f, account_type: val })} data-testid={`account-type-${val}`}
                      className={`py-2.5 px-3 text-xs font-medium border transition-colors ${f.account_type === val ? "border-[#FF0055] bg-[#FF0055]/10 text-white" : "border-white/15 text-neutral-400 hover:text-white"}`}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <input required placeholder="Your name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="owner-name" className={inputCls} />
              <input placeholder={f.account_type === "operator" ? "Tour company name (optional)" : "Business / practice name"} value={f.business_name} onChange={(e) => setF({ ...f, business_name: e.target.value })} data-testid="owner-business" className={inputCls} />
            </>
          )}
          <input required type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="owner-email" className={inputCls} />
          <input required type="password" placeholder="Password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="owner-password" className={inputCls} />
          {error && <div className="text-[#FF0055] text-sm" data-testid="owner-auth-error">{error}</div>}
          <Button type="submit" disabled={busy} data-testid="owner-submit" className="w-full bg-[#FF0055] hover:bg-[#ff2d74] rounded-none h-11 font-semibold">
            <Store size={15} /> {busy ? "Please wait..." : mode === "register" ? "Create Account" : "Sign In"}
          </Button>
        </form>
      </div>
    </div>
  );
}
